import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { useUnit } from 'effector-react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useLocation } from 'react-router-dom';
import {
  $mapLoaded,
  resetMapLoadedEv,
  setMapLoadedEv,
} from '../../models/mapModel/index.js';
import {
  createObject,
  customStylesLight,
  customStylesDark,
  deleteAll,
  ExtendDrawBar,
  modeChangeHandler,
  changeDrawStyles,
  updatePolygon,
} from '../../utils/draw_polygon_utils.js';
import {
  $isDarkTheme,
  $isTablet,
  $userInfo,
  delayedShowTabletBarEv,
} from '../../models/authModel/index.js';
import { mapCenterPC, mapCenterTab } from '../../utils/mapbox-utils.js';
import { setMapRefEv } from '../../models/educationModel/index.js';
import bc from '../../assets/map_icons/bc.png';
import stops from '../../assets/map_icons/stops.png';
import tc from '../../assets/map_icons/tc.png';
import trc from '../../assets/map_icons/trc.png';
import parking from '../../assets/map_icons/parking.png';
import CH_popup from '../../assets/CH_popup.png';
import ch_mobile_popup from '../../assets/ch_mobile_popup.png';
import ch_mobile_popup_active from '../../assets/ch_mobile_popup_active.png';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZnJvbnRpZXJkIiwiYSI6ImNrYTNuMjBvNjAxNnAzbW85N3ZsbmRvZmYifQ.9OWZ7YWGJ4DDIV4bCTwsaA';

function Map() {
  const mapContainer = useRef(null);

  const setMapLoaded = useUnit(setMapLoadedEv);
  const resetMapLoaded = useUnit(resetMapLoadedEv);
  const mapLoaded = useUnit($mapLoaded);
  const darkMode = useUnit($isDarkTheme);
  const userInfo = useUnit($userInfo);
  const isTablet = useUnit($isTablet);
  const delayedShowTabletBar = useUnit(delayedShowTabletBarEv);
  const { pathname } = useLocation();
  const setMapRef = useUnit(setMapRefEv);

  useEffect(() => {
    if (window.map) return;
    window.map = new mapboxgl.Map({
      container: mapContainer.current,
      // style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      style: darkMode
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/streets-v12',
      // center: [68.71584352632227, 48.58775813682156],
      // center: [76.92861, 43.25667],
      center:
        pathname === '/center-home'
          ? [65.7133351434909, 46.271747202152454]
          : isTablet
            ? mapCenterTab
            : mapCenterPC,
      zoom: pathname === '/center-home' ? 4 : 10,
      projection: pathname === '/ab-markets' ? 'mercator' : 'globe',
    });
    setMapRef({
      element: 'map',
      ref: mapContainer.current,
    });
    window.map.dragRotate.disable();

    // const geocoder = new MapboxGeocoder({
    //   accessToken: mapboxgl.accessToken,
    //   mapboxgl,
    // });
    //
    // const scale = new mapboxgl.NavigationControl({
    //   showCompass: false,
    // });

    // window.map.addControl(scale, 'bottom-right');
    // window.map.addControl(geocoder, 'top-left');

    window.map.on('style.load', () => {
      window.map.loadImage(stops, (error, image) => {
        if (error) throw error;
        window.map.addImage('stops', image);
      });
      window.map.loadImage(tc, (error, image) => {
        if (error) throw error;
        window.map.addImage('tc', image);
      });
      window.map.loadImage(bc, (error, image) => {
        if (error) throw error;
        window.map.addImage('bc', image);
      });
      window.map.loadImage(trc, (error, image) => {
        if (error) throw error;
        window.map.addImage('trc', image);
      });
      window.map.loadImage(parking, (error, image) => {
        if (error) throw error;
        window.map.addImage('public_parking', image);
      });
      window.map.loadImage(CH_popup, (error, image) => {
        if (error) throw error;
        window.map.addImage('popup', image, {
          stretchX: [
            [25, 55],
            [85, 115],
          ],
          stretchY: [[25, 100]],
          content: [25, 25, 115, 100],
          pixelRatio: 2,
        });
      });
      window.map.loadImage(ch_mobile_popup, (error, image) => {
        if (error) throw error;
        window.map.addImage('mobile_popup', image, {
          content: [6, 4, 78, 32],
        });
      });

      window.map.loadImage(ch_mobile_popup_active, (error, image) => {
        if (error) throw error;
        window.map.addImage('mobile_popup_active', image, {
          content: [6, 4, 78, 32],
        });
      });

      setMapLoaded(true);
      delayedShowTabletBar();
    });

    // window.map.on('draw.render', () => {
    //   const { layers } = window.map.getStyle();
    //   if (layers.some((item) => item.id.includes('gl-draw'))) {
    //     console.log('TEST:: I am triggered');
    //   }
    // });

    return () => {
      resetMapLoaded();
      delete window.map;
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && Object.keys(userInfo).length > 0 && !window.draw) {
      const Draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          point: true,
          trash: true,
        },
        // FIXME Custom styles of points and polygons MADE BY controls
        // for ref look in geo
        styles: userInfo.dark_theme ? customStylesDark : customStylesLight,
      });

      window.draw = Draw;

      const drawControls = new ExtendDrawBar({
        draw: Draw,
        buttons: [
          {
            on: 'click',
            action: deleteAll,
            classes: ['mapbox-gl-draw_ctrl-draw-btn', 'mapbox-gl-draw_trash'],
          },
        ],
      });

      const { modes } = MapboxDraw;
      // modes.simple_select.onDrag = function (state, e) {};

      if (window.map._controls.length != 3) {
        window.map.addControl(drawControls, 'bottom-left');
      }

      window.addEventListener('keyup', (e) => {
        if (window.draw.getMode() !== 'simple_select' && e.key === 'Escape') {
          window.draw.changeMode('simple_select');
        }
      });
      window.map.on('draw.modechange', modeChangeHandler);
      window.map.on('draw.create', createObject);
      window.map.on('draw.delete', deleteAll);
      window.map.on('draw.update', updatePolygon);
      // if (userInfo.perms.includes('admin')) {
      //   window.map.on('zoomstart', () => {
      //     window.zoom_start = window.map.getZoom();
      //   });
      //
      //   window.map.on('zoomend', () => {
      //     window.zoom_start = null;
      //   });
      //
      //   window.map.on('zoom', () => {
      //     const zoom = window.map.getZoom();
      //     if (window.zoom_start) {
      //       if (window.zoom_start > zoom) {
      //         if (zoom < 13 && zoom > 11) {
      //           window.map.flyTo({
      //             zoom: 11,
      //             speed: 5,
      //           });
      //         } else if (zoom < 11 && zoom > 10) {
      //           window.map.flyTo({
      //             zoom: 10,
      //             speed: 5,
      //           });
      //         }
      //       } else if (window.zoom_start < zoom) {
      //         if (zoom > 10 && zoom < 11) {
      //           window.map.flyTo({
      //             zoom: 11.5,
      //             speed: 5,
      //           });
      //         } else if (zoom > 12 && zoom < 13) {
      //           window.map.flyTo({
      //             zoom: 13.5,
      //             speed: 5,
      //           });
      //         }
      //       }
      //     }
      //   });
      // }
    }
  }, [userInfo, mapLoaded]);

  return <div ref={mapContainer} className="map-container" />;
}

export default Map;
